import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking");
    const { clear } = useCart();

    useEffect(() => {
        if (!sessionId) {
            setState("failed");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const res = await api.get(`/payments/status/${sessionId}`);
                if (res.data.payment_status === "paid") {
                    setState("paid");
                    clear();
                    return;
                }
            } catch {}
            attempts += 1;
            if (attempts < 10) setTimeout(poll, 2000);
            else setState("failed");
        };
        poll();
    }, [sessionId]);

    return (
        <main data-testid="payment-success-page" className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-start justify-center px-6 py-24 lg:px-10">
            {state === "checking" && (
                <p className="font-serif text-3xl italic text-clay" data-testid="payment-checking">Confirming your order…</p>
            )}
            {state === "paid" && (
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                    <CheckCircle2 size={40} className="text-gold" strokeWidth={1.5} />
                    <h1 className="mt-8 font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl" data-testid="order-confirmed-heading">
                        Thank <span className="italic text-espresso">you.</span>
                    </h1>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-clay">
                        Your order is confirmed. We'll wrap your piece in recycled tissue, tuck in a handwritten note, and ship it within 3–5 days.
                    </p>
                    <Link
                        to="/shop"
                        data-testid="success-continue-shopping"
                        className="group mt-10 inline-flex items-center gap-3 bg-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso"
                    >
                        Keep browsing
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            )}
            {state === "failed" && (
                <div data-testid="payment-unconfirmed">
                    <XCircle size={40} className="text-clay" strokeWidth={1.5} />
                    <h1 className="mt-8 font-serif text-4xl font-light tracking-tighter">We couldn't confirm your payment yet.</h1>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-clay">
                        If you completed checkout, your confirmation may take a moment — check your email, or reach out and we'll sort it out.
                    </p>
                    <Link to="/shop" className="mt-10 inline-flex items-center gap-3 border border-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-espresso hover:text-sand">
                        Back to the collection
                    </Link>
                </div>
            )}
        </main>
    );
}

export function PaymentCancel() {
    return (
        <main data-testid="payment-cancel-page" className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-start justify-center px-6 py-24 lg:px-10">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">Checkout cancelled</p>
                <h1 className="mt-6 font-serif text-5xl font-light tracking-tighter text-gold md:text-6xl">
                    No <span className="italic text-espresso">worries.</span>
                </h1>
                <p className="mt-6 max-w-md text-base leading-relaxed text-clay">
                    Your bag is still saved — nothing was charged. Take your time; the yarn isn't going anywhere.
                </p>
                <Link
                    to="/shop"
                    data-testid="cancel-return-shop"
                    className="group mt-10 inline-flex items-center gap-3 bg-espresso px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-sand transition-colors duration-300 hover:bg-gold hover:text-espresso"
                >
                    Return to the collection
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </motion.div>
        </main>
    );
}
